import React from "react";
import uiUtils from "./uiUtils";
import moment from "moment";

const warningStyles = {
  backgroundColor: "#e9e9e9",
  border: "1px solid #d2d3d6",
  padding: "28px 16px 28px 21px",
  width: "100%",
};

const possibleNaFieldsMap = {
  formUI9089: [
    // Section A
    "empTradeName",
    "empAddr2",
    "empProvince",
    "empPhoneext",
    // Section B
    "empPocMiddleName",
    "empPocAddr2",
    "empPocProvince",
    "empPocPhoneExt",
    // Section C
    "attyAgMiddleName",
    "attyAgAddress2",
    "attyAgProvince",
    "attyAgPhoneExt",
    "attyAgFEIN",
    "attyAgStateBarNumber",
    "attyAgGoodStandingState",
    "attyAgGoodStandingAttorney",
    // Section E
    "jobOppWageConditions",
    // Section F.a
    "primaryWorksiteAddr1",
    "primaryWorksiteAddr2",
    "primaryWorksiteCity",
    "primaryWorksiteCounty",
    "primaryWorksiteState",
    "primaryWorksitePostalCode",
    "primaryWorksiteBlsAreaCode",
    "primaryWorksiteBlsArea",
    // Section H.c
    "swaJobOrderStartDate",
    "swaJobOrderEndDate",
    "sundayPaperExists",
    "newspaperNameForAd",
    "newspaperAdPlacedDate",
    "advertisementMethod",
    "journalNameForAd",
    "journalAdPlacedDate",
    // Section H.d
    "jobFairFrom",
    "jobFairTo",
    "employerWebsiteFrom",
    "employerWebsiteTo",
    "jobSearchWebsiteFrom",
    "jobSearchWebsiteTo",
    "onCampusRecruitingFrom",
    "onCampusRecruitingTo",
    "tradeProfessionalFrom",
    "tradeProfessionalTo",
    "privateEmploymentFrom",
    "privateEmploymentTo",
    "employeeReferralFrom",
    "employeeReferralTo",
    "campusPlacementFrom",
    "campusPlacementTo",
    "localEthnicNewspaperFrom",
    "localEthnicNewspaperTo",
    "radioTvAdFrom",
    "radioTvAdTo",
    // Section J
    "prepLastName",
    "prepfirstName",
    "prepMiddleName",
    "prepLawFirmFEIN",
    "prepFirmBusinessName",
    "prepEmail",
    // Appendix A.A
    "appAContAddress2",
    "appAContProvince",
    // Appendices Array Machine Names
    "fwEducationAppB",
    "fwTrainingAppC",
    "fwSkillsAppD",
    "fwExpAppE",
  ],
};

const isEmptyAppendix = (value) =>
  Array.isArray(value) && (value.length === 0 || !value?.[0]);

const isAppendix = (fieldCode, value) =>
  fieldCode.toLowerCase().includes("appendix") && Array.isArray(value);

const isIncompleteAppendix = (value) => {
  if (Array.isArray(value) && value.length !== 0 && !!value?.[0]) {
    const val = value.find((entry) => {
      const nonNullishFields = Object.keys(entry).filter(
        (field) => !!entry[field]
      );
      return nonNullishFields.length === 1;
    });
    return !!val;
  }
  return false;
};

const possibleNAFieldsForIncompleteAppendix = (
  form_ui_name,
  appField,
  appValue
) => {
  const na_regex = /^[Nn]\/[Aa]$/;
  if (form_ui_name === "formUI9089") {
    const ret = [];
    const appendicesIncompleteConditions = {
      fwEducationAppB: {
        field: "appAEduTypeId",
        value: "None",
        naFields: ["1.a", "1.b", "1.c", "1.d"],
      },
      fwTrainingAppC: {
        field: "appATrainingInstName",
        isNA: true,
        naFields: ["1.a", "1.b"],
      },
      fwSkillsAppD: {
        field: "appASkillsInstName",
        isNA: true,
        naFields: ["1.a", "1.b", "1.c"],
      },
      fwExpAppE: {
        field: "appAExpEmpName",
        isNA: true,
        naFields: ["1.a", "1.b", "1.c", "1.e", "1.f", "1.g", "1.h", "1.l"],
      },
    };
    if (!!appendicesIncompleteConditions[appField]) {
      const { field, value, isNA, naFields } =
        appendicesIncompleteConditions[appField];

      if (isEmptyAppendix(appValue) || !appValue) {
        ret.push({ entryNumber: 1, naFields: ["1", ...naFields] });
        return ret;
      }
      if (!Array.isArray(appValue)) {
        return [];
      }

      appValue.forEach((entry, idx) => {
        const entryNumber = idx + 1;
        if (!!value && entry[field] === value) {
          ret.push({ entryNumber, naFields });
        }
        if (!!isNA && entry[field].match(na_regex)) {
          ret.push({ entryNumber, naFields });
        }
      });
      return ret;
    }
  }
  return [];
};

const appendixAD9089HasAValue = (values) => {
  const appendixDFields = [
    "appDForeignWorkerDate",
    "appDPublication1",
    "appDPublicationDate1",
    "appDPublication2",
    "appDPublicationDate2",
    "appDPublication3",
    "appDPublicationDate3",
    "appDRecruitmentInfo",
  ];
  const firstValue = appendixDFields.find((field) => !!values[field]);
  return !!firstValue;
};

const removeFromBaseNaFieldMap = (fields, map) => {
  fields.forEach((field) => {
    const index = map.indexOf(field);
    if (index !== -1) {
      map.splice(index, 1);
    }
  });
};

const determinePossibleNaFields = (form_ui_name, values) => {
  if (form_ui_name === "formUI9089") {
    const sectionCFields = [
      "attyAgMiddleName",
      "attyAgAddress2",
      "attyAgProvince",
      "attyAgPhoneExt",
      "attyAgFEIN",
      "attyAgStateBarNumber",
      "attyAgGoodStandingState",
      "attyAgGoodStandingAttorney",
    ];
    let basePossibleNaPopulation = possibleNaFieldsMap[form_ui_name];

    if (!!values.attyAgRepType && values.attyAgRepType === "None") {
      const temp = [...basePossibleNaPopulation];
      removeFromBaseNaFieldMap(sectionCFields, temp);
      basePossibleNaPopulation = temp;
    }

    if (appendixAD9089HasAValue(values)) {
      const appendixDFields = [
        "appDPublication1",
        "appDPublication2",
        "appDPublication3",
        "appDRecruitmentInfo",
      ];
      basePossibleNaPopulation = [
        ...basePossibleNaPopulation,
        ...appendixDFields,
      ];
    }
    return basePossibleNaPopulation;
  }
};

const renderDateValidations = (
  form_ui_name,
  values,
  errors,
  uiSchema_lookup
) => {
  let prevPageTitleIdx = -1;
  if (form_ui_name !== "formUI9089") {
    return null;
  }
  const warningTexts = {
    rangeWarning:
      "The recruitment steps must be conducted at least 30 days, but no more than 180 days, before filing the application.",
    comparisonWarning:
      "The duration of start to end date must be at least 30 days.",
    sundayWarning: "Advertisement date has to be a Sunday.",
    uniqueSundayWarning:
      "Newspaper advertisements must be two different Sundays.",
  };

  const warningValidators = {
    checkDateRange: ({ dateField, rangeConditions = true }) => {
      const value = values?.[dateField];
      const hasError = errors?.[dateField];

      if (!!dateField && rangeConditions && !!value && !hasError) {
        const today = moment();
        const dateToCheck = moment(value);
        const diff = Math.round(today.diff(dateToCheck, "days", true));
        if (diff > 180 || diff < 30) {
          return <li>{warningTexts?.rangeWarning}</li>;
        }
      }
      return null;
    },
    checkDateComparison: ({ startDate, endDate, hasComparisonWarning }) => {
      if (hasComparisonWarning) {
        const value = values?.[startDate];
        const valueToCheck = values?.[endDate];
        const hasError = errors?.[startDate];

        if (!!value && !hasError) {
          const start = moment(value);
          const end = moment(valueToCheck);
          const diff = Math.round(end.diff(start, "days", true)) + 1;
          if (diff < 30) {
            return <li>{warningTexts?.comparisonWarning}</li>;
          }
        }
      }
      return null;
    },
    checkSunday: ({ dateField, sundaryConditions, hasSundayWarning }) => {
      if (hasSundayWarning) {
        const value = values?.[dateField];
        const hasError = errors?.[dateField];

        if (!!value && !hasError) {
          const dayOfWeek = moment(value).day();
          if (dayOfWeek !== 0 && sundaryConditions) {
            return <li>{warningTexts?.sundayWarning}</li>;
          }
        }
      }
      return null;
    },
    checkUniqueSunday: ({
      dateField,
      uniqueSundayConditions,
      hasUniqueSundayWarning,
    }) => {
      if (hasUniqueSundayWarning) {
        const { value, valueToCheck, condition } = uniqueSundayConditions;
        const hasError = errors?.[dateField];

        if (!!value && !hasError) {
          const sameDate = moment(value).isSame(valueToCheck);
          if (sameDate && condition) {
            return <li>{warningTexts?.uniqueSundayWarning}</li>;
          }
        }
      }
      return null;
    },
  };

  const dateSchema = [
    {
      sectionTitle:
        "H.c: Professional/Non-Professional Recruitment Information",
      displayLabel: true,
      dateFields: [
        {
          dateField: "swaJobOrderStartDate",
        },
        {
          dateField: "swaJobOrderEndDate",
          startDate: "swaJobOrderStartDate",
          endDate: "swaJobOrderEndDate",
          hasComparisonWarning: true,
        },
        {
          dateField: "newspaperAdPlacedDate",
          hasSundayWarning: true,
          sundaryConditions: values.sundayPaperExists === "yes",
        },
        {
          dateField: "journalAdPlacedDate",
          hasSundayWarning: true,
          sundaryConditions: values.sundayPaperExists === "yes",
          hasUniqueSundayWarning: true,
          uniqueSundayConditions: {
            value: values.journalAdPlacedDate,
            valueToCheck: values.newspaperAdPlacedDate,
            condition:
              values.advertisementMethod === "newspaper" &&
              values.sundayPaperExists === "yes",
          },
        },
      ],
    },
    {
      sectionTitle:
        "H.d: Additional Recruitment Requirements for Professional Occupations",
      displayLabel: false,
      dateFields: [
        { dateField: "jobFairFrom" },
        { dateField: "jobFairTo" },
        {
          dateField: "employerWebsiteFrom",
        },
        {
          dateField: "employerWebsiteTo",
        },
        {
          dateField: "jobSearchWebsiteFrom",
        },
        {
          dateField: "jobSearchWebsiteTo",
        },
        {
          dateField: "onCampusRecruitingFrom",
        },
        {
          dateField: "onCampusRecruitingTo",
        },
        {
          dateField: "tradeProfessionalFrom",
        },
        {
          dateField: "tradeProfessionalTo",
        },
        {
          dateField: "privateEmploymentFrom",
        },
        {
          dateField: "privateEmploymentTo",
        },
        {
          dateField: "employeeReferralFrom",
        },
        {
          dateField: "employeeReferralTo",
        },
        {
          dateField: "campusPlacementFrom",
        },
        {
          dateField: "campusPlacementTo",
        },
        {
          dateField: "localEthnicNewspaperFrom",
        },
        {
          dateField: "localEthnicNewspaperTo",
        },
        {
          dateField: "radioTvAdFrom",
        },
        {
          dateField: "radioTvAdTo",
        },
      ],
    },
  ];
  let allWarnings = [];
  dateSchema.forEach(({ sectionTitle, dateFields, displayLabel }) => {
    let renderedFieldWarnings = [];
    dateFields.forEach(
      ({
        dateField,
        startDate,
        endDate,
        rangeConditions,
        hasComparisonWarning,
        hasSundayWarning,
        sundaryConditions,
        hasUniqueSundayWarning,
        uniqueSundayConditions,
      }) => {
        const renderRangeWarning = warningValidators?.checkDateRange({
          dateField,
          endDate,
          rangeConditions,
        });
        const renderComparisonWarning = warningValidators?.checkDateComparison({
          startDate,
          endDate,
          hasComparisonWarning,
        });

        const renderSundayWarning = warningValidators?.checkSunday({
          dateField,
          sundaryConditions,
          hasSundayWarning,
        });
        const renderRenderSundayWarning = warningValidators?.checkUniqueSunday({
          dateField: startDate,
          uniqueSundayConditions,
          hasUniqueSundayWarning,
        });

        if (
          renderRangeWarning ||
          renderComparisonWarning ||
          renderSundayWarning ||
          renderRenderSundayWarning
        ) {
          const fieldLabel =
            uiSchema_lookup?.[dateField]?.[2] !== "no label" && displayLabel
              ? uiSchema_lookup?.[dateField]?.[2]
              : null;

          const fieldCode = !!uiSchema_lookup?.[dateField]?.[1]
            ? uiUtils.removeAsteriskAndConditionalSymbols(
                uiSchema_lookup?.[dateField]?.[1]
              )
            : fieldLabel;
          const currPageIdx = uiSchema_lookup?.[dateField]?.[0];
          const renderSectionHeader = prevPageTitleIdx !== currPageIdx;
          if (renderSectionHeader) {
            prevPageTitleIdx = currPageIdx;
          }

          renderedFieldWarnings.push(
            <li style={{ margin: "10px 0" }}>
              Field {fieldCode}. {fieldLabel}
              <ul>
                {renderRangeWarning}
                {renderComparisonWarning}
                {renderSundayWarning}
                {renderRenderSundayWarning}
              </ul>
            </li>
          );
        }
      }
    );
    if (!!renderedFieldWarnings?.length) {
      allWarnings.push({
        sectionTitle,
        renderedFieldWarnings,
      });
    }
  });
  if (!!allWarnings?.length) {
    return (
      <ul>
        {allWarnings.map(({ sectionTitle, renderedFieldWarnings }) => (
          <li>
            {sectionTitle}
            <ul>{renderedFieldWarnings}</ul>
          </li>
        ))}
      </ul>
    );
  }
};

const AppendixList = ({ appendixNaInfo }) => {
  return (
    appendixNaInfo.length !== 0 &&
    appendixNaInfo.map((entry, i) => (
      <ul key={i}>
        <li style={{ margin: "0.75rem 0rem" }}>
          {`Entry ${entry.entryNumber}`}
          {entry.naFields.map((fieldCode, j) => (
            <ul key={j}>
              <li
                style={{ margin: "0.75rem 0rem" }}
              >{`Field ${uiUtils.removeAsteriskAndConditionalSymbols(
                fieldCode
              )}`}</li>
            </ul>
          ))}
        </li>
      </ul>
    ))
  );
};

const FormWarnings = ({
  values,
  errors,
  uiSchema_lookup,
  uiSchema_nav,
  form_ui_name,
}) => {
  let prevPageTitleIdx = -1;

  const isSpecialField = (field) => {
    if (form_ui_name === "formUI9089") {
      const sectionFaFields = [
        "primaryWorksiteAddr1",
        "primaryWorksiteAddr2",
        "primaryWorksiteCity",
        "primaryWorksiteCounty",
        "primaryWorksiteState",
        "primaryWorksitePostalCode",
        "primaryWorksiteBlsAreaCode",
        "primaryWorksiteBlsArea",
      ];
      return (
        (field === "isAppdendixBAttached" && values[field] === 0) ||
        (sectionFaFields.includes(field) && values[field] === "N/A")
      );
    }
    return false;
  };

  const possibleNaFields = determinePossibleNaFields(form_ui_name, values);
  const renderedDateValidations = renderDateValidations(
    form_ui_name,
    values,
    errors,
    uiSchema_lookup,
    uiSchema_nav
  );

  return (
    <>
      <div className="margin-top-5" style={warningStyles}>
        <strong>Note:</strong> These blank fields will be populated with N/A or
        0, as applicable:
        <ul>
          {possibleNaFields
            .filter((field) => {
              return (
                values[field] === null ||
                values[field] === undefined ||
                isEmptyAppendix(values[field]) ||
                isIncompleteAppendix(values[field]) ||
                values[field] === "" ||
                isSpecialField(field)
              );
            })
            .sort(
              (prev, next) =>
                uiSchema_lookup[prev]?.[0] - uiSchema_lookup[next]?.[0]
            )
            .map((field, idx) => {
              const fieldLabel =
                uiSchema_lookup[field]?.[2] !== "no label"
                  ? uiSchema_lookup[field]?.[2]
                  : null;
              const fieldCode = !!uiSchema_lookup[field]?.[1]
                ? uiSchema_lookup[field]?.[1]
                : fieldLabel;
              const currPageIdx = uiSchema_lookup[field]?.[0];

              const renderSectionHeader = prevPageTitleIdx !== currPageIdx;
              if (renderSectionHeader) {
                prevPageTitleIdx = currPageIdx;
              }
              const sectionHeader = `${uiSchema_nav[currPageIdx]?.prefix}: ${uiSchema_nav[currPageIdx]?.name}`;

              return (
                <>
                  {!!renderSectionHeader &&
                    !sectionHeader.includes("undefined") && (
                      <li style={{ margin: "0.75rem 0rem" }}>
                        {uiUtils.removeLineBreak(sectionHeader)}
                      </li>
                    )}
                  {isAppendix(fieldCode, values[field]) && (
                    <AppendixList
                      appendixNaInfo={possibleNAFieldsForIncompleteAppendix(
                        form_ui_name,
                        field,
                        values[field]
                      )}
                    />
                  )}
                  {!!fieldCode && !isAppendix(fieldCode, values[field]) && (
                    <ul>
                      <li key={idx} style={{ margin: "0.75rem 0rem" }}>
                        {`Field ${uiUtils.removeAsteriskAndConditionalSymbols(
                          fieldCode
                        )}`}
                      </li>
                    </ul>
                  )}
                </>
              );
            })}
        </ul>
      </div>
      {renderedDateValidations && (
        <div className="margin-top-5" style={warningStyles}>
          {renderedDateValidations}
        </div>
      )}
    </>
  );
};

export default FormWarnings;
